import React, { useState, useEffect, useCallback } from 'react';
import { FINANCIAL_API, authFetch } from '../constants';
import { useDrillDown, DrillDownRow } from '../components/DrillDown';
import TabBar from '../components/ui/TabBar';
import ActionButton from '../components/ui/ActionButton';
import FilterBar from '../components/ui/FilterBar';
import { useGlobalEntity } from '../hooks/useGlobalEntity';

const CONSOLIDATED = '__CONSOLIDATED__';

const fmt = (n: number) =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

const fmtSigned = (n: number) =>
  n < 0 ? `(${fmt(Math.abs(n))})` : fmt(n);

type ViewMode = 'before' | 'conversion' | 'reporting' | 'adjustments' | 'after' | 'after_all' | 'comparison';

export default function TrialBalance() {
  const { entity: globalEntity } = useGlobalEntity();
  const [entities, setEntities] = useState<any[]>([]);
  const [entity, setEntity] = useState('');
  const [periods, setPeriods] = useState<string[]>([]);
  const [period, setPeriod] = useState('');
  const [view, setView] = useState<ViewMode>('after');
  const [data, setData] = useState<any>(null);
  const [beforeData, setBeforeData] = useState<any>(null);
  const [afterData, setAfterData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [showUSD, setShowUSD] = useState(false);
  const [exporting, setExporting] = useState<string | null>(null);
  const [pendingInfo, setPendingInfo] = useState<any>(null);
  const [showExportJE, setShowExportJE] = useState(false);
  const [jeExporting, setJeExporting] = useState(false);
  const [jeFormat, setJeFormat] = useState<'generic' | 'netsuite'>('generic');
  const [jeStatus, setJeStatus] = useState<'CONFIRMED' | 'PROVISIONAL' | 'ALL'>('CONFIRMED');

  const drill = useDrillDown();

  const entityCurrency = entities.find(e => e.entity_code === entity)?.functional_currency || '';
  const isUSD = entity === CONSOLIDATED || entityCurrency === 'USD';
  const isConsolidated = entity === CONSOLIDATED;
  const isComparison = view === 'comparison';

  useEffect(() => {
    authFetch(`${FINANCIAL_API}/entities`)
      .then(r => r.json())
      .then(rows => {
        setEntities(rows);
        if (rows.length > 0 && !entity) setEntity(globalEntity || rows[0].entity_code);
      });
  }, []); // eslint-disable-line

  useEffect(() => {
    if (globalEntity && globalEntity !== entity) setEntity(globalEntity);
  }, [globalEntity]); // eslint-disable-line

  useEffect(() => {
    if (!entity) return;
    const url = isConsolidated
      ? `${FINANCIAL_API}/consolidated/available-periods`
      : `${FINANCIAL_API}/available-periods?entity_code=${entity}`;
    authFetch(url)
      .then(r => r.json())
      .then(d => {
        setPeriods(d.periods || []);
        if (d.periods?.length) setPeriod(d.periods[d.periods.length - 1]);
      });
    setShowUSD(false);
  }, [entity, isConsolidated]);

  const buildUrl = useCallback((v: string) => {
    const currSuffix = showUSD && !isUSD ? '&currency=USD' : '';
    return isConsolidated
      ? `${FINANCIAL_API}/consolidated/trial-balance?period=${period}&view=${v}${currSuffix}`
      : `${FINANCIAL_API}/trial-balance?entity_code=${entity}&period=${period}&view=${v}${currSuffix}`;
  }, [entity, period, showUSD, isUSD, isConsolidated]);

  const loadData = useCallback(() => {
    if (!entity || !period) return;
    setLoading(true);
    drill.resetDrill();

    if (isComparison) {
      Promise.all([
        authFetch(buildUrl('before')).then(r => r.json()),
        authFetch(buildUrl('after')).then(r => r.json()),
      ]).then(([b, a]) => {
        setBeforeData(b);
        setAfterData(a);
        setData(a);
        setLoading(false);
      }).catch(() => setLoading(false));
    } else {
      authFetch(buildUrl(view))
        .then(r => r.json())
        .then(d => { setData(d); setLoading(false); })
        .catch(() => setLoading(false));
    }
  }, [entity, period, view, showUSD, isUSD, isConsolidated, isComparison, buildUrl]); // eslint-disable-line

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (!entity) return;
    const url = isConsolidated
      ? `${FINANCIAL_API}/consolidated/pending-adjustments`
      : `${FINANCIAL_API}/pending-adjustments?entity_code=${entity}`;
    authFetch(url).then(r => r.json()).then(setPendingInfo).catch(() => setPendingInfo(null));
  }, [entity, isConsolidated]);

  const handleExportJE = async () => {
    setJeExporting(true);
    try {
      const params = new URLSearchParams({ format: jeFormat, status: jeStatus });
      if (!isConsolidated) params.set('entity', entity);
      if (period) {
        const [y, m] = period.split('-');
        params.set('period_year', y);
        params.set('period_month', m);
      }
      const resp = await authFetch(`${FINANCIAL_API}/export/journal-entries?${params}`);
      if (!resp.ok) throw new Error('Export failed');
      const blob = await resp.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `journal-entries-${entity}-${period}-${jeFormat}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
      setShowExportJE(false);
    } catch (e) { console.error(e); }
    finally { setJeExporting(false); }
  };

  const handleExport = async (format: 'pdf' | 'excel') => {
    setExporting(format);
    try {
      const body = {
        statement: 'trial-balance',
        entity_code: isConsolidated ? 'CONSOLIDATED' : entity,
        period, view,
        currency: showUSD && !isUSD ? 'USD' : undefined,
      };
      const resp = await authFetch(`${FINANCIAL_API}/export/${format}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `trial-balance-${entity}-${period}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { console.error('Export failed:', err); }
    setExporting(null);
  };

  const needsConversion = (() => {
    if (isConsolidated) return true;
    const ent = entities.find(e => e.entity_code === entity);
    return ent && ent.local_framework && ent.local_framework !== ent.reporting_framework;
  })();

  const allViewButtons: { key: ViewMode; label: string }[] = [
    { key: 'before', label: 'Local' },
    { key: 'conversion', label: 'Conversion' },
    { key: 'reporting', label: 'Reporting' },
    { key: 'adjustments', label: 'Adjustments' },
    { key: 'after', label: 'After' },
    { key: 'after_all', label: 'After (All)' },
    { key: 'comparison', label: 'Comparison' },
  ];
  const viewButtons = allViewButtons.filter(v =>
    needsConversion || !['conversion', 'reporting'].includes(v.key)
  );

  const comparisonRows = isComparison && beforeData && afterData ? (() => {
    const beforeMap: Record<string, any> = {};
    for (const a of (beforeData.accounts || [])) beforeMap[a.account_code] = a;
    const afterMap: Record<string, any> = {};
    for (const a of (afterData.accounts || [])) afterMap[a.account_code] = a;
    const allCodes = new Set([...Object.keys(beforeMap), ...Object.keys(afterMap)]);
    return Array.from(allCodes).sort().map(code => {
      const b = beforeMap[code] || { balance: 0, total_debits: 0, total_credits: 0 };
      const a = afterMap[code] || { balance: 0, total_debits: 0, total_credits: 0 };
      return {
        account_code: code,
        account_name: (a.account_name || b.account_name || ''),
        account_type: (a.account_type || b.account_type || ''),
        statement: (a.statement || b.statement || ''),
        before_balance: b?.balance ?? 0,
        after_balance: a?.balance ?? 0,
        impact: (a?.balance ?? 0) - (b?.balance ?? 0),
      };
    });
  })() : [];

  const impactColor = (impact: number, acctType: string) => {
    if (Math.abs(impact) < 0.005) return 'var(--text-dimmer)';
    const isReverse = ['LIABILITY', 'EXPENSE'].includes(acctType);
    const isGood = isReverse ? impact < 0 : impact > 0;
    return isGood ? 'var(--accent-good)' : 'var(--accent-critical)';
  };

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--navy-900)', margin: 0 }}>
          Trial Balance
        </h2>
        {data && (
          <p style={{ fontSize: 13, color: 'var(--text-hint)', margin: '4px 0 0' }}>
            {data.entity_name} &middot; Period: {period} &middot; Currency: {data.currency}
            {isComparison && ' \u00b7 Comparison: Before vs After'}
          </p>
        )}
      </div>

      <FilterBar filters={[
        {
          key: 'entity', label: 'Entity', type: 'select', value: entity,
          onChange: v => setEntity(v),
          options: [
            ...entities.map(e => ({ value: e.entity_code, label: `${e.entity_name} (${e.functional_currency})` })),
            { value: CONSOLIDATED, label: 'Consolidated (USD)' },
          ],
          placeholder: 'Select Entity',
        },
        {
          key: 'period', label: 'Period', type: 'select', value: period,
          onChange: v => setPeriod(v),
          options: periods.map(p => ({ value: p, label: p })),
          placeholder: 'Select Period',
        },
      ]} />

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 20, flexWrap: 'wrap' }}>
        <TabBar
          tabs={viewButtons.map(v => ({ key: v.key, label: v.label }))}
          active={view}
          onChange={k => setView(k as ViewMode)}
        />
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        {!isUSD && (
          <ActionButton
            label={showUSD ? 'USD' : entityCurrency}
            onClick={() => setShowUSD(!showUSD)}
            variant={showUSD ? 'primary' : 'secondary'}
            size="sm"
          />
        )}
        <ActionButton label={exporting === 'pdf' ? '...' : 'PDF'} onClick={() => handleExport('pdf')} variant="ghost" size="sm" disabled={!!exporting || !data} />
        <ActionButton label={exporting === 'excel' ? '...' : 'Excel'} onClick={() => handleExport('excel')} variant="ghost" size="sm" disabled={!!exporting || !data} />
        <ActionButton label="CSV" onClick={async () => {
          try {
            const p = new URLSearchParams({ entity_code: entity, period, format: 'csv' });
            const resp = await authFetch(`${FINANCIAL_API.replace('/api/financial', '/api/v1')}/reports/trial-balance/export?${p}`);
            if (!resp.ok) throw new Error('Export failed');
            const blob = await resp.blob();
            const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
            a.download = `trial-balance-${entity}-${period}.csv`; a.click(); URL.revokeObjectURL(a.href);
          } catch (e) { console.error(e); }
        }} variant="ghost" size="sm" disabled={!data} />
        <ActionButton label="Export JEs" onClick={() => setShowExportJE(true)} variant="secondary" size="sm" />
        {loading && <span style={{ fontSize: 12, color: 'var(--text-hint)' }}>Loading...</span>}
      </div>

      {/* Provisional warning banner */}
      {view === 'after_all' && (
        <div style={{ background: 'var(--warning-bg)', border: '1px solid var(--warning-border)', borderRadius: 8, padding: '10px 16px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 16 }}>!</span>
          <span style={{ fontSize: 12, color: 'var(--warning)' }}>This view includes <strong>PROVISIONAL</strong> adjustments not yet confirmed by a supervisor.</span>
        </div>
      )}

      {/* Pending adjustments card */}
      {pendingInfo && (pendingInfo.provisional_jes?.count > 0 || pendingInfo.pending_actions?.count > 0 || pendingInfo.unresolved_exceptions?.total > 0) && (
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 8, padding: '10px 16px', marginBottom: 12, display: 'flex', gap: 24, alignItems: 'center', fontSize: 12 }}>
          <span style={{ color: 'var(--text-hint)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Pending</span>
          {pendingInfo.provisional_jes?.count > 0 && (
            <span style={{ color: 'var(--warning)' }}>{pendingInfo.provisional_jes.count} provisional JE{pendingInfo.provisional_jes.count !== 1 ? 's' : ''}</span>
          )}
          {pendingInfo.pending_actions?.count > 0 && (
            <span style={{ color: 'var(--navy-700)' }}>{pendingInfo.pending_actions.count} action{pendingInfo.pending_actions.count !== 1 ? 's' : ''} in review</span>
          )}
          {pendingInfo.unresolved_exceptions?.total > 0 && (
            <span style={{ color: 'var(--text-hint)' }}>{pendingInfo.unresolved_exceptions.total} unresolved exception{pendingInfo.unresolved_exceptions.total !== 1 ? 's' : ''}</span>
          )}
        </div>
      )}

      {/* Export JE modal */}
      {showExportJE && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.15)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setShowExportJE(false)}>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 12, padding: 24, width: 400 }}
            onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px', fontSize: 16, color: 'var(--text-primary)' }}>Export Journal Entries</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <label style={{ fontSize: 12, color: 'var(--text-hint)' }}>
                Entity: <strong style={{ color: 'var(--text-primary)' }}>{isConsolidated ? 'All Entities' : entity}</strong>
              </label>
              <label style={{ fontSize: 12, color: 'var(--text-hint)' }}>
                Period: <strong style={{ color: 'var(--text-primary)' }}>{period || 'All'}</strong>
              </label>
              <div>
                <label style={{ fontSize: 12, color: 'var(--text-hint)', display: 'block', marginBottom: 4 }}>JE Status</label>
                <select value={jeStatus} onChange={e => setJeStatus(e.target.value as any)}
                  style={{ width: '100%', padding: '6px 10px', fontSize: 13, background: 'var(--bg-card)', color: 'var(--text-primary)', border: '1px solid var(--border-subtle)', borderRadius: 6 }}>
                  <option value="CONFIRMED">Confirmed only</option>
                  <option value="PROVISIONAL">Provisional only</option>
                  <option value="ALL">All statuses</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: 12, color: 'var(--text-hint)', display: 'block', marginBottom: 4 }}>Format</label>
                <select value={jeFormat} onChange={e => setJeFormat(e.target.value as any)}
                  style={{ width: '100%', padding: '6px 10px', fontSize: 13, background: 'var(--bg-card)', color: 'var(--text-primary)', border: '1px solid var(--border-subtle)', borderRadius: 6 }}>
                  <option value="generic">Generic CSV</option>
                  <option value="netsuite">NetSuite CSV</option>
                </select>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <ActionButton label={jeExporting ? 'Exporting...' : 'Download CSV'} onClick={handleExportJE} disabled={jeExporting} />
                <ActionButton label="Cancel" onClick={() => setShowExportJE(false)} variant="secondary" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* === COMPARISON VIEW === */}
      {isComparison && comparisonRows.length > 0 && (
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Account Code</th>
                <th style={{ ...thStyle, textAlign: 'left' }}>Account Name</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Before</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>After</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Impact</th>
              </tr>
            </thead>
            <tbody>
              {comparisonRows.map(r => (
                <tr key={r.account_code} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                  <td style={tdStyle} className="mono">{r.account_code}</td>
                  <td style={{ ...tdStyle, color: 'var(--text-primary)' }}>{r.account_name}</td>
                  <td style={tdRight}>{fmtSigned(r.before_balance)}</td>
                  <td style={tdRight}>{fmtSigned(r.after_balance)}</td>
                  <td style={{ ...tdRight, fontWeight: 600, color: impactColor(r.impact, r.account_type) }}>
                    {Math.abs(r.impact) < 0.005 ? '\u2014' : fmtSigned(r.impact)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: '2px solid var(--border-subtle)', background: 'rgba(0,0,0,0.03)' }}>
                <td style={{ ...tdStyle, fontWeight: 700 }}></td>
                <td style={{ ...tdStyle, fontWeight: 700, color: 'var(--text-primary)' }}>TOTAL</td>
                <td style={{ ...tdRight, fontWeight: 700 }}>{fmtSigned((beforeData?.total_debits ?? 0) - (beforeData?.total_credits ?? 0))}</td>
                <td style={{ ...tdRight, fontWeight: 700 }}>{fmtSigned((afterData?.total_debits ?? 0) - (afterData?.total_credits ?? 0))}</td>
                <td style={{ ...tdRight, fontWeight: 700, color: 'var(--text-hint)' }}>{'\u2014'}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* === NORMAL VIEW === */}
      {!isComparison && data && data.accounts && (
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Account Code</th>
                <th style={{ ...thStyle, textAlign: 'left' }}>Account Name</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Debits</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Credits</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Balance</th>
              </tr>
            </thead>
            <tbody>
              {data.accounts.map((a: any) => (
                <React.Fragment key={a.account_code}>
                  <tr
                    onClick={() => drill.toggleAccount(a.account_code, entity, period, view, isConsolidated)}
                    style={{
                      borderBottom: '1px solid var(--border-subtle)',
                      cursor: 'pointer',
                      background: drill.expandedAcct === a.account_code ? 'var(--accent-subtle)' : 'transparent',
                    }}
                    onMouseEnter={e => { if (drill.expandedAcct !== a.account_code) (e.currentTarget as HTMLElement).style.background = 'var(--bg-hover)'; }}
                    onMouseLeave={e => { if (drill.expandedAcct !== a.account_code) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                  >
                    <td style={tdStyle} className="mono">
                      <span style={{ color: 'var(--accent)', marginRight: 6, fontSize: 10 }}>
                        {drill.expandedAcct === a.account_code ? '\u25BC' : '\u25B6'}
                      </span>
                      {a.account_code}
                    </td>
                    <td style={{ ...tdStyle, color: 'var(--text-primary)' }}>{a.account_name}</td>
                    <td style={tdRight}>{fmt(a.total_debits)}</td>
                    <td style={tdRight}>{fmt(a.total_credits)}</td>
                    <td style={{
                      ...tdRight,
                      color: a.balance < 0 ? 'var(--accent-critical)' : 'var(--text-primary)',
                      fontWeight: 600,
                    }}>
                      {a.balance < 0 ? `(${fmt(Math.abs(a.balance))})` : fmt(a.balance)}
                    </td>
                  </tr>

                  {drill.expandedAcct === a.account_code && (
                    <DrillDownRow
                      colSpan={5}
                      isLoading={drill.acctLoading}
                      detail={drill.acctDetail}
                      isConsolidated={isConsolidated}
                      expandedJE={drill.expandedJE}
                      jeDetail={drill.jeDetail}
                      jeLoading={drill.jeLoading}
                      toggleJE={drill.toggleJE}
                    />
                  )}
                </React.Fragment>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: '2px solid var(--border-subtle)', background: 'var(--accent-subtle)' }}>
                <td style={{ ...tdStyle, fontWeight: 700 }}></td>
                <td style={{ ...tdStyle, fontWeight: 700, color: 'var(--text-primary)' }}>TOTAL</td>
                <td style={{ ...tdRight, fontWeight: 700 }}>{fmt(data.total_debits)}</td>
                <td style={{ ...tdRight, fontWeight: 700 }}>{fmt(data.total_credits)}</td>
                <td style={{ ...tdRight, fontWeight: 700 }}>{fmt(data.total_debits - data.total_credits)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* Balance check */}
      {!isComparison && data && (
        <div style={{
          marginTop: 16, padding: '10px 16px', borderRadius: 8,
          background: data.is_balanced ? 'var(--positive-bg)' : 'var(--negative-bg)',
          border: `1px solid ${data.is_balanced ? 'var(--accent-good)' : 'var(--accent-critical)'}`,
          fontSize: 13, fontWeight: 600,
          color: data.is_balanced ? 'var(--accent-good)' : 'var(--accent-critical)',
        }}>
          {data.is_balanced
            ? `Trial Balance is balanced (Debits = Credits = ${fmt(data.total_debits)})`
            : `Trial Balance is NOT balanced! Debits: ${fmt(data.total_debits)}, Credits: ${fmt(data.total_credits)}`
          }
        </div>
      )}

      {/* Comparison summary */}
      {isComparison && beforeData && afterData && (
        <div style={{
          marginTop: 16, padding: '10px 16px', borderRadius: 8,
          background: 'var(--accent-subtle)',
          border: '1px solid var(--accent-border)',
          fontSize: 13, fontWeight: 600, color: 'var(--accent)',
        }}>
          Comparison: {comparisonRows.filter(r => Math.abs(r.impact) >= 0.005).length} accounts impacted by Agent Workforce adjustments
        </div>
      )}

      {data && !isComparison && data.accounts?.length === 0 && !loading && (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-hint)', fontSize: 14 }}>
          No data for this entity/period/view combination.
        </div>
      )}
    </div>
  );
}

const thStyle: React.CSSProperties = {
  padding: '10px 14px', textAlign: 'left', fontSize: 11,
  fontWeight: 600, color: 'var(--text-hint)',
  textTransform: 'uppercase', letterSpacing: '0.04em',
  borderBottom: '2px solid var(--border-subtle)',
};

const tdStyle: React.CSSProperties = {
  padding: '10px 14px', fontSize: 13, color: 'var(--text-secondary)',
};

const tdRight: React.CSSProperties = {
  ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums',
};
